/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import { AiOutlineMore } from "react-icons/ai";
import { TablePagination } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { IconWrapper } from "../../customHooks/useIcon";

interface TableRowData {
  [key: string]: string | number;
}

interface TableTemplateProps {
  data: TableRowData[];
  className?: string;
  columns: string[];
  columnToStyle?: number; // Index of the column to style
  columnCustomStyle?: React.CSSProperties; // Custom style for the whole column
  extraAction?: boolean;
  onActionClick?: (row: TableRowData) => void;
  actionType?: "openLink" | "showPopUp";
  extraActionPopUpContent?: (row: any) => React.ReactNode;
  makeRowClickable?: boolean;
  rowLink?: string | ((row: TableRowData) => void);
  extraActionIcon?: React.ReactNode;
  styleCell?: (value: string | number, column: string) => React.CSSProperties; // Custom style for individual cells
  totalCount: number;
  rowsPerPage?: number;
  page?: number;
  onPageChange?: (page: number) => void;
}

const TableTemplate: React.FC<TableTemplateProps> = ({
  className,
  data,
  columns,
  columnToStyle,
  columnCustomStyle,
  makeRowClickable,
  rowLink,
  extraAction = false,
  extraActionPopUpContent,
  onActionClick,
  actionType,
  extraActionIcon = <IconWrapper icon={AiOutlineMore} />,
  styleCell,
  rowsPerPage = 7,
  page = 1,
  onPageChange,
  totalCount,
}) => {
  const navigate = useNavigate();
  const [visiblePopUpIndex, setVisiblePopUpIndex] = React.useState<
    number | null
  >(null);
  const popUpRef = React.useRef<HTMLDivElement | null>(null);

  // Close popup on outside click
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popUpRef.current &&
        !popUpRef.current.contains(event.target as Node)
      ) {
        setVisiblePopUpIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Calculate pagination state
  const zeroBasedPage = page - 1;
  const lastPage = Math.ceil(totalCount / rowsPerPage);
  const hasNextPage = page < lastPage;
  const hasPreviousPage = page > 1;

  // Function to handle page changes
  const handlePageChange = (event: unknown, newPage: number) => {
    if (onPageChange) {
      onPageChange(newPage + 1); // Convert to one-based for your API
    }
  };

  return (
    <>
      <TableContainer component={Paper} className={`today_start ${className}`}>
        <Table
          aria-label="dynamic table"
          className="!overflow-y-visible !overflow-x-scroll"
          style={{ overflow: "visible" }}
        >
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={column}
                  sx={{
                    fontWeight: "bold",
                    borderRadius:
                      index === 0
                        ? "8px 0 0 8px"
                        : index === columns.length - 1 && !extraAction
                        ? "0 8px 8px 0"
                        : "0",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  {column}
                </TableCell>
              ))}
              {extraAction && (
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: "bold",
                    borderRadius: "0 8px 8px 0",
                    backgroundColor: "#f5f5f5",
                  }}
                ></TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody className="!overflow-y-visible">
            {data.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                className="!overflow-y-visible"
                sx={{ "&:last-child td, &:last-child th": { border: "none" } }}
                hover={makeRowClickable} // MUI style for hover effect
                style={makeRowClickable ? { cursor: "pointer" } : {}}
                onClick={() => {
                  if (makeRowClickable) {
                    if (typeof rowLink === "function") {
                      rowLink(row);
                    } else if (typeof rowLink === "string") {
                      navigate(rowLink);
                    }
                  }
                }}
              >
                {columns.map((column, colIndex) => {
                  const cellStyle =
                    columnToStyle === colIndex ? columnCustomStyle : undefined;

                  const conditionalStyle = styleCell
                    ? styleCell(row[column], column)
                    : undefined;

                  return (
                    <TableCell
                      key={column}
                      sx={{
                        ...cellStyle,
                        ...conditionalStyle,
                      }}
                    >
                      {row[column]}
                    </TableCell>
                  );
                })}
                {extraAction && (
                  <TableCell align="right" className="relative">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation(); // prevent row click
                        actionType === "showPopUp"
                          ? setVisiblePopUpIndex(
                              visiblePopUpIndex === rowIndex ? null : rowIndex
                            )
                          : onActionClick && onActionClick(row);
                      }}
                    >
                      {extraActionIcon}
                    </IconButton>
                    {visiblePopUpIndex === rowIndex && (
                      <div
                        ref={popUpRef}
                        className="absolute right-[5px] z-10 bottom-[-70px] flex text-sm items-center justify-center gap-4 p-2 flex-col min-w-[8rem] min-h-[5rem] bg-white rounded-lg shadow-md border border-primary-border"
                      >
                        {extraActionPopUpContent &&
                          extraActionPopUpContent(row)}
                      </div>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {totalCount > rowsPerPage && (
        <TablePagination
          component="div"
          count={totalCount}
          page={zeroBasedPage}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
          slotProps={{
            actions: {
              previousButton: {
                disabled: !hasPreviousPage,
              },
              nextButton: {
                disabled: !hasNextPage,
              },
            },
          }}
        />
      )}
    </>
  );
};

export default TableTemplate;
